import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Grid,
  CircularProgress,
} from "@mui/material";
import {
  LandingGearTypeCatalogItem,
  SubAssembly,
} from "../../../../types/subAssembly";
import { PendingComponentSubAssembly } from "../../../../types/component";
import { useSubAssemblyService } from "../../../../services/subAssemblyService";
import {
  filterSupportedAirframeFamilies,
  normalizeAirframeFamily,
  sameAirframeFamily,
  useAirframeFamilies,
} from "../hooks/useSubAssemblyCatalog";
import { useLandingGearTypes } from "../hooks/useLandingGearTypes";
import {
  formatSubAssemblyDisplayLabel,
  isSubAssemblyAttached,
  normalizeLandingGearTypeCode,
  sameSubAssemblyType,
  resolveLockedLandingGearType,
  validateSubAssemblyAttach,
} from "../../../../utils/subAssemblyUtils";

type Props = {
  open: boolean;
  pendingSubAssemblies: PendingComponentSubAssembly[];
  defaultAirframeFamily?: string;
  onClose: () => void;
  onAttach: (item: PendingComponentSubAssembly) => void;
};

const ACCENT = "hsl(19 101% 51% / 1)";

const fieldSx = {
  "& .MuiInputLabel-root": {
    color: "#bbb",
    lineHeight: 1.2,
    overflow: "visible",
  },
  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    backgroundColor: "#111",
    paddingInline: "4px",
    transform: "translate(14px, -9px) scale(0.75)",
  },
  "& .MuiInputLabel-root.Mui-focused": { color: ACCENT },
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    "& fieldset": { borderColor: "#555" },
    "&:hover fieldset": { borderColor: "#888" },
    "&.Mui-focused fieldset": { borderColor: ACCENT },
    "&.Mui-disabled": {
      color: "#fff",
      "& fieldset": { borderColor: "#555" },
    },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "#fff",
    WebkitTextFillColor: "#fff",
    opacity: 1,
  },
  "& .MuiSvgIcon-root": { color: "#fff" },
} as const;

const emptyForm = {
  airframeFamily: "",
  landingGearType: "",
  subAssemblyType: "",
  subAssemblyId: "",
};

export function AttachSubAssemblyDialog({
  open,
  pendingSubAssemblies,
  defaultAirframeFamily,
  onClose,
  onAttach,
}: Readonly<Props>) {
  const { getSubAssemblies } = useSubAssemblyService();
  const { families, loading: familiesLoading } = useAirframeFamilies();
  const [form, setForm] = useState(emptyForm);
  const [catalogItems, setCatalogItems] = useState<SubAssembly[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const supportedFamilies = useMemo(() => {
    const current = normalizeAirframeFamily(String(defaultAirframeFamily ?? ""));
    return filterSupportedAirframeFamilies([
      ...families,
      ...(current ? [current] : []),
    ]);
  }, [families, defaultAirframeFamily]);
  const hasLockedAirframeFamily = Boolean(
    normalizeAirframeFamily(String(defaultAirframeFamily ?? ""))
  );

  const airframeFamily = normalizeAirframeFamily(
    form.airframeFamily || defaultAirframeFamily || ""
  );
  const { landingGearTypes, loading: typesLoading } =
    useLandingGearTypes(airframeFamily);

  const lockedLandingGearType = resolveLockedLandingGearType(pendingSubAssemblies);
  const alreadyAttachedIds = useMemo(
    () => pendingSubAssemblies.map((item) => item.id),
    [pendingSubAssemblies]
  );
  const excludeSubAssemblyTypes = useMemo(
    () => pendingSubAssemblies.map((item) => item.subAssemblyType),
    [pendingSubAssemblies]
  );

  const availableLandingGearTypes = useMemo(() => {
    if (!lockedLandingGearType) {
      return landingGearTypes;
    }

    const matched = landingGearTypes.filter(
      (item) =>
        normalizeLandingGearTypeCode(item.type) === lockedLandingGearType
    );

    if (matched.length > 0) {
      return matched;
    }

    return [
      {
        type: lockedLandingGearType,
        label: lockedLandingGearType,
        allowedSubAssemblyTypes: [],
      },
    ] satisfies LandingGearTypeCatalogItem[];
  }, [landingGearTypes, lockedLandingGearType]);

  const landingGearTypeValue =
    form.landingGearType || lockedLandingGearType || "";

  useEffect(() => {
    if (!open) return;
    setForm({
      airframeFamily: normalizeAirframeFamily(defaultAirframeFamily ?? ""),
      landingGearType: lockedLandingGearType ?? "",
      subAssemblyType: "",
      subAssemblyId: "",
    });
    setError(null);
    setCatalogItems([]);
  }, [open, lockedLandingGearType, defaultAirframeFamily]);

  const allowedSubAssemblyTypes = useMemo(() => {
    const catalogMatch = landingGearTypes.find(
      (item) =>
        normalizeLandingGearTypeCode(item.type) ===
        normalizeLandingGearTypeCode(landingGearTypeValue)
    );

    const types = catalogMatch?.allowedSubAssemblyTypes ?? [];

    return types.filter(
      (type) =>
        !excludeSubAssemblyTypes.some((existing) =>
          sameSubAssemblyType(existing, type)
        )
    );
  }, [landingGearTypes, landingGearTypeValue, excludeSubAssemblyTypes]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!landingGearTypeValue || !form.subAssemblyType) {
      setCatalogItems((prev) => (prev.length === 0 ? prev : []));
      setCatalogLoading((prev) => (prev ? false : prev));
      return;
    }

    let active = true;
    setCatalogLoading(true);
    setError(null);

    void getSubAssemblies(
      landingGearTypeValue,
      form.subAssemblyType,
      false,
      airframeFamily
    )
      .then((items) => {
        if (!active) return;

        const familyFiltered = airframeFamily
          ? items.filter(
              (item) =>
                sameAirframeFamily(String(item.airframeFamily ?? ""), airframeFamily)
            )
          : items;

        const allowedTypeSet = new Set(
          allowedSubAssemblyTypes.map((type) => type.trim().toUpperCase())
        );

        const filtered = allowedTypeSet.size
          ? familyFiltered.filter((item) =>
              allowedTypeSet.has(item.subAssemblyType.trim().toUpperCase())
            )
          : familyFiltered;

        setCatalogItems(filtered);
      })
      .catch((err) => {
        if (active) {
          setCatalogItems([]);
          const message =
            err instanceof Error && err.message
              ? err.message
              : "Failed to load sub-assemblies. Please try again.";
          setError(message);
        }
      })
      .finally(() => {
        if (active) setCatalogLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    open,
    airframeFamily,
    landingGearTypeValue,
    form.subAssemblyType,
    getSubAssemblies,
    allowedSubAssemblyTypes,
  ]);

  const availableCatalogItems = useMemo(() => {
    const blocked = new Set(alreadyAttachedIds);
    return catalogItems.filter((item) => {
      if (!item.id || blocked.has(item.id)) return false;
      if (isSubAssemblyAttached(item)) return false;
      if (
        form.subAssemblyType &&
        !sameSubAssemblyType(item.subAssemblyType, form.subAssemblyType)
      ) {
        return false;
      }
      return true;
    });
  }, [catalogItems, alreadyAttachedIds, form.subAssemblyType]);

  const handleAirframeFamilyChange = (value: string) => {
    setForm({
      airframeFamily: normalizeAirframeFamily(value),
      landingGearType: lockedLandingGearType ?? "",
      subAssemblyType: "",
      subAssemblyId: "",
    });
  };

  const handleLandingGearTypeChange = (value: string) => {
    if (lockedLandingGearType) {
      return;
    }

    setForm((prev) => {
      const allowed =
        landingGearTypes.find((item) => item.type === value)
          ?.allowedSubAssemblyTypes ?? [];
      const keepSubType = allowed.includes(prev.subAssemblyType)
        ? prev.subAssemblyType
        : "";
      return {
        ...prev,
        landingGearType: value,
        subAssemblyType: keepSubType,
        subAssemblyId: "",
      };
    });
  };

  const handleAttach = () => {
    if (!airframeFamily) {
      setError("Airframe type is required.");
      return;
    }
    if (!form.landingGearType && !lockedLandingGearType) {
      setError("Type of landing gear is required.");
      return;
    }
    if (!form.subAssemblyType) {
      setError("Sub-assembly type is required.");
      return;
    }
    if (!form.subAssemblyId) {
      setError("Select a sub-assembly to attach.");
      return;
    }

    const selected = catalogItems.find((item) => item.id === form.subAssemblyId);
    if (!selected) {
      setError("Selected sub-assembly is no longer available.");
      return;
    }

    const pendingItem: PendingComponentSubAssembly = {
      id: selected.id,
      landingGearType: selected.landingGearType,
      subAssemblyType: selected.subAssemblyType,
      serialNumber: selected.serialNumber,
      partNumber: selected.partNumber,
    };

    const validation = validateSubAssemblyAttach(pendingSubAssemblies, pendingItem);

    if (!validation.ok) {
      setError(validation.message);
      return;
    }

    onAttach(pendingItem);
    onClose();
  };

  const catalogLoadingState = familiesLoading || typesLoading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { backgroundColor: "#111", color: "#fff", border: "1px solid #333" },
      }}
    >
      <DialogTitle sx={{ color: ACCENT }}>Attach Sub-Assembly</DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              label="Airframe Type"
              value={airframeFamily}
              onChange={(e) => handleAirframeFamilyChange(e.target.value)}
              fullWidth
              disabled={catalogLoadingState || hasLockedAirframeFamily}
              sx={fieldSx}
              InputLabelProps={{ shrink: Boolean(airframeFamily) }}
            >
              {supportedFamilies.map((family) => (
                <MenuItem key={family} value={family}>
                  {family}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              label="Type of Landing Gear"
              value={landingGearTypeValue}
              onChange={(e) => handleLandingGearTypeChange(e.target.value)}
              fullWidth
              disabled={catalogLoadingState || Boolean(lockedLandingGearType) || !airframeFamily}
              sx={fieldSx}
              InputLabelProps={{ shrink: Boolean(landingGearTypeValue) }}
            >
              {availableLandingGearTypes.map((item) => (
                <MenuItem key={item.type} value={item.type}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              label="Type"
              value={form.subAssemblyType}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  subAssemblyType: e.target.value,
                  subAssemblyId: "",
                }))
              }
              fullWidth
              disabled={!landingGearTypeValue || !airframeFamily}
              sx={fieldSx}
            >
              {allowedSubAssemblyTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              label="Sub-Assembly"
              value={form.subAssemblyId}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, subAssemblyId: e.target.value }))
              }
              fullWidth
              disabled={
                !landingGearTypeValue ||
                !form.subAssemblyType ||
                !airframeFamily ||
                catalogLoading
              }
              sx={fieldSx}
              InputProps={{
                endAdornment: catalogLoading ? (
                  <CircularProgress size={18} sx={{ mr: 2 }} />
                ) : undefined,
              }}
            >
              {availableCatalogItems.length === 0 ? (
                <MenuItem disabled value="">
                  {catalogLoading
                    ? "Loading..."
                    : form.subAssemblyType
                      ? "No available sub-assemblies for this type"
                      : "Select a type first"}
                </MenuItem>
              ) : (
                availableCatalogItems.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {formatSubAssemblyDisplayLabel(item)}
                  </MenuItem>
                ))
              )}
            </TextField>
          </Grid>
        </Grid>

        {error && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ color: "#f44336", fontSize: "0.85rem" }}>
              {error}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ color: "#fff" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAttach}
          sx={{
            backgroundColor: ACCENT,
            "&:hover": { backgroundColor: "hsl(19 101% 41% / 1)" },
          }}
        >
          Attach
        </Button>
      </DialogActions>
    </Dialog>
  );
}
